import React, { useState, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

const Sidebar = () => {
  const [expanded, setExpanded] = useState(false);
  const sidebarRef = useRef(null);
  const sidebarTextRefs = useRef([]);
  const location = useLocation();

  gsap.registerPlugin(useGSAP);

  useGSAP(() => {
    const sidebar = sidebarRef.current;

    const handleMouseEnter = () => {
      gsap.to(sidebar, {
        width: "16rem", // Expanded width
        duration: 0.1, // Animation duration
        ease: "power2.inOut", // Smooth easing function
        onStart: () => {
          sidebarTextRefs.current.forEach((text) => {
            if (text) {
              gsap.to(text, {
                opacity: 1,
                visibility: "visible",
                duration: 0.4,
                delay: 0.1, // Slight delay for smoother transition
              });
            }
          });
        },
        onComplete: () => {
          setExpanded(true);
        },
      });
    };

    const handleMouseLeave = () => {
      gsap.to(sidebar, {
        width: "6rem", // Collapsed width
        duration: 0.1, // Animation duration
        ease: "power2.inOut", // Smooth easing function
        onStart: () => {
          sidebarTextRefs.current.forEach((text) => {
            if (text) {
              gsap.to(text, {
                opacity: 0,
                visibility: "hidden",
                //duration: 0.2,
              });
            }
          });
        },
        onComplete: () => {
          setExpanded(false);
        },
      });
    };

    sidebar.addEventListener("mouseenter", handleMouseEnter);
    sidebar.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      sidebar.removeEventListener("mouseenter", handleMouseEnter);
      sidebar.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <div
      ref={sidebarRef}
      className={`fixed top-16 left-0 h-full bg-white shadow-lg z-20 flex flex-col items-start w-24 transition-all duration-100`}
    >
      <ul className="space-y-6 w-full overflow-y-auto max-h-[calc(100vh-5rem)] pt-8 pb-8">
        {/* Dashboard Link */}
        <li>
          <NavLink
            to="/admin/dashboard"
            className={({ isActive }) =>
              `group flex items-center w-full px-8 py-3 transition-all  ${
                isActive
                  ? "bg-blue-100 text-blue-800"
                  : "hover:bg-blue-100 text-gray-700"
              }`
            }
          >
            <i className="fas fa-home text-2xl w-10 h-10 flex justify-center items-center"></i>
            <span
              ref={(el) => (sidebarTextRefs.current[0] = el)}
              className={`ml-1 text-sm font-medium ${expanded ? "" : "hidden"}`}
            >
              Dashboard
            </span>
          </NavLink>
        </li>

        {/* consultations */}
        <li>
          <NavLink
            to="/admin/consultations"
            className={({ isActive }) =>
              `group flex items-center w-full px-8 py-3 transition-all  ${
                isActive
                  ? "bg-blue-100 text-blue-800"
                  : "hover:bg-blue-100 text-gray-700"
              }`
            }
          >
            <i className="fas fa-comments text-2xl w-10 h-10 flex justify-center items-center"></i>
            <span
              ref={(el) => (sidebarTextRefs.current[1] = el)}
              className={`ml-1 text-sm font-medium ${expanded ? "" : "hidden"}`}
            >
              Consultations
            </span>
          </NavLink>
        </li>

        {/* Apply for Application Link */}
        <li>
          <NavLink
            to="/admin/applications"
            className={({ isActive }) =>
              `group flex items-center w-full px-8 py-3 transition-all  ${
                isActive ||
                location.pathname.includes("/admin/application-details")
                  ? "bg-blue-100 text-blue-800"
                  : "hover:bg-blue-100 text-gray-700"
              }`
            }
          >
            <i className="fas fa-file-alt text-2xl w-10 h-10 flex justify-center items-center"></i>
            <span
              ref={(el) => (sidebarTextRefs.current[1] = el)}
              className={`ml-1 text-sm font-medium ${expanded ? "" : "hidden"}`}
            >
              Applications
            </span>
          </NavLink>
        </li>

        {/*  Consultant */}
        <li>
          <NavLink
            to="/admin/consultants"
            className={({ isActive }) =>
              `group flex items-center w-full px-8 py-3 transition-all  ${
                isActive || location.pathname.includes("/admin/addconsultant")
                  ? "bg-blue-100 text-blue-800"
                  : "hover:bg-blue-100 text-gray-700"
              }`
            }
          >
            <i className="fas fa-user-tie text-2xl w-10 h-10 flex justify-center items-center"></i>
            <span
              ref={(el) => (sidebarTextRefs.current[1] = el)}
              className={`ml-1 text-sm font-medium ${expanded ? "" : "hidden"}`}
            >
              Consultants
            </span>
          </NavLink>
        </li>

        {/*  Students */}
        <li>
          <NavLink
            to="/admin/students"
            className={({ isActive }) =>
              `group flex items-center w-full px-8 py-3 transition-all  ${
                isActive
                  ? "bg-blue-100 text-blue-800"
                  : "hover:bg-blue-100 text-gray-700"
              }`
            }
          >
            <i className="fas fa-user-graduate text-2xl w-10 h-10 flex justify-center items-center"></i>

            <span
              ref={(el) => (sidebarTextRefs.current[1] = el)}
              className={`ml-1 text-sm font-medium ${expanded ? "" : "hidden"}`}
            >
              Students
            </span>
          </NavLink>
        </li>

        {/*  Universities */}
        <li>
          <NavLink
            to="/admin/universities"
            className={({ isActive }) =>
              `group flex items-center w-full px-8 py-3 transition-all  ${
                isActive || location.pathname.includes("/admin/adduni")
                  ? "bg-blue-100 text-blue-800"
                  : "hover:bg-blue-100 text-gray-700"
              }`
            }
          >
            <i className="fas fa-university text-2xl w-10 h-10 flex justify-center items-center"></i>
            <span
              ref={(el) => (sidebarTextRefs.current[1] = el)}
              className={`ml-1 text-sm font-medium ${expanded ? "" : "hidden"}`}
            >
              Universities
            </span>
          </NavLink>
        </li>

        {/*  Calculator */}
        <li>
          <NavLink
            to="/admin/calculator"
            className={({ isActive }) =>
              `group flex items-center w-full px-8 py-3 transition-all  ${
                isActive ||
                location.pathname.includes("/admin/calculator-details")
                  ? "bg-blue-100 text-blue-800"
                  : "hover:bg-blue-100 text-gray-700"
              }`
            }
          >
            <i className="fas fa-calculator text-2xl w-10 h-10 flex justify-center items-center"></i>
            <span
              ref={(el) => (sidebarTextRefs.current[1] = el)}
              className={`ml-1 text-sm font-medium ${expanded ? "" : "hidden"}`}
            >
              Calculator
            </span>
          </NavLink>
        </li>

        {/*  Sales Persons */}
        <li>
          <NavLink
            to="/admin/sales-persons"
            className={({ isActive }) =>
              `group flex items-center w-full px-8 py-3 transition-all  ${
                isActive
                  ? "bg-blue-100 text-blue-800"
                  : "hover:bg-blue-100 text-gray-700"
              }`
            }
          >
            <i className="fas fa-user-tag text-2xl w-10 h-10 flex justify-center items-center"></i>
            <span
              ref={(el) => (sidebarTextRefs.current[1] = el)}
              className={`ml-1 text-sm font-medium ${expanded ? "" : "hidden"}`}
            >
              Sales Persons
            </span>
          </NavLink>
        </li>

        {/*  CRM */}
        <li>
          <NavLink
            to="/admin/crm"
            className={({ isActive }) =>
              `group flex items-center w-full px-8 py-3 transition-all  ${
                isActive || location.pathname.includes("/admin/crm")
                  ? "bg-blue-100 text-blue-800"
                  : "hover:bg-blue-100 text-gray-700"
              }`
            }
          >
            <i className="fas fa-chart-line text-2xl w-10 h-10 flex justify-center items-center"></i>
            <span
              ref={(el) => (sidebarTextRefs.current[1] = el)}
              className={`ml-1 text-sm font-medium ${expanded ? "" : "hidden"}`}
            >
              CRM
            </span>
          </NavLink>
        </li>
      </ul>
    </div>
  );
};

export default Sidebar;
